import { memo, useState } from "react";
import "../styles/emailsummary.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMAIL_STATUS_FILTER = {
  SENT: 1,
  DELIVERED: 2,
  OPENED: 3,
  UNOPENED: 4,
  CLICKED: 5,
  SPAM_REPORTED: 6,
  BOUNCED: 7,
  UNSUBSCRIBED: 8,
};

/** Rate formulas shown in each tile's tooltip. */
const TILE_FORMULAS = {
  Delivered: "Delivered / Sent",
  Opened: "Unique Opens / Delivered",
  Clicked: "Unique Clickers / Delivered",
  "Spam Reported": "Spam Reports / Delivered",
  Bounced: "Bounced / Sent",
  Unsubscribed: "Unsubscribed / Sent",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Maps raw summary API data to flat named values. */
function buildSummaryData(summary) {
  const sent = summary?.emailsSent ?? 0;
  const delivered = summary?.delivered ?? 0;
  const opened = summary?.opened ?? 0;
  const clicked = summary?.urlClicked ?? 0;
  const spamReported = summary?.spamReported ?? 0;
  const bounced = summary?.bounced ?? 0;
  const unsubscribed = summary?.unsubscribed ?? 0;
  const unopened = delivered - opened;

  return {
    sent,
    delivered,
    opened,
    unopened,
    clicked,
    spamReported,
    bounced,
    unsubscribed,
  };
}

/** Builds the ordered tile descriptor array from derived summary data. */
function buildTiles({
  sent,
  delivered,
  opened,
  unopened,
  clicked,
  spamReported,
  bounced,
  unsubscribed,
}) {
  const rate = (num, total) =>
    total > 0 ? `${((num / total) * 100).toFixed(1)}%` : "—";

  return [
    {
      title: "Sent",
      filterKey: EMAIL_STATUS_FILTER.SENT,
      value: sent,
      subtitle: " ",
      color: "var(--fill-success-default, #43A35C)",
    },
    {
      title: "Delivered",
      filterKey: EMAIL_STATUS_FILTER.DELIVERED,
      value: delivered,
      subtitle: `Delivered ${rate(delivered, sent)}`,
      color: "var(--fill-success-default, #43A35C)",
    },
    {
      title: "Opened",
      filterKey: EMAIL_STATUS_FILTER.OPENED,
      value: opened,
      subtitle: `Open Rate ${rate(opened, delivered)}`,
      color: "var(--color-blue-80, #1854A8)",
    },
    {
      title: "Unopened",
      filterKey: EMAIL_STATUS_FILTER.UNOPENED,
      value: unopened,
      subtitle: " ",
      color: "var(--color-blue-80, #1854A8)",
    },
    {
      title: "Clicked",
      filterKey: EMAIL_STATUS_FILTER.CLICKED,
      value: clicked,
      subtitle: `Click Thru Rate ${rate(clicked, delivered)}`,
      color: "var(--fill-alert-default, #E67337)",
    },
    {
      title: "Spam Reported",
      filterKey: EMAIL_STATUS_FILTER.SPAM_REPORTED,
      value: spamReported,
      subtitle: `Spam Rate ${rate(spamReported, delivered)}`,
      color: "var(--fill-alert-default, #E67337)",
    },
    {
      title: "Bounced",
      filterKey: EMAIL_STATUS_FILTER.BOUNCED,
      value: bounced,
      subtitle: `Bounce Rate ${rate(bounced, sent)}`,
      color: "var(--fill-danger-default, #C93434)",
    },
    {
      title: "Unsubscribed",
      filterKey: EMAIL_STATUS_FILTER.UNSUBSCRIBED,
      value: unsubscribed,
      subtitle: `Unsubscribe Rate ${rate(unsubscribed, sent)}`,
      color: "var(--fill-danger-default, #C93434)",
    },
  ];
}

/** Returns the tooltip string for a given tile title. */
function getTileTooltip(title) {
  const formula = TILE_FORMULAS[title];
  const timestamp = new Date().toLocaleString();
  return formula
    ? `Formula: ${formula}\nLast Updated: ${timestamp}`
    : `Last Updated: ${timestamp}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmailTile({ tile, isSelected, onClick }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") onClick();
  };

  return (
    <div
      className={`summary-tile-wrapper${isSelected ? " selected" : ""}`}
      onClick={onClick}
      onKeyDown={handleKey}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      title={getTileTooltip(tile.title)}
    >
      <div className="summary-tile">
        <div className="summary-tile-title">{tile.title}</div>
        <div className="summary-tile-body">
          <div className="summary-tile-value" style={{ color: tile.color }}>
            {tile.value}
          </div>
          <div className="summary-tile-subtitle">{tile.subtitle}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function EmailSummary({ summary, onFilterChange }) {
  const [selectedTile, setSelectedTile] = useState(null);

  const handleTileClick = (filterKey) => {
    setSelectedTile(filterKey);
    onFilterChange?.(filterKey);
  };

  const tiles = buildTiles(buildSummaryData(summary));

  return (
    <div className="email-summary-container">
      {tiles.map((tile) => (
        <EmailTile
          key={tile.filterKey}
          tile={tile}
          isSelected={selectedTile === tile.filterKey}
          onClick={() => handleTileClick(tile.filterKey)}
        />
      ))}
    </div>
  );
}

export default memo(EmailSummary);
